import { KuxMenuItem } from '@kaltura-ott/tvpil';
import { action } from 'mobx';
import { container, singleton } from 'tsyringe';

import type { Linear } from '../stores/entities/Linear';
import type { ParentalPinStoreDepreciated } from '../stores/parentalPin/parental-pin-store-depreciated';
import { ParentalPinType } from '../types/stores/parental-pin-store-type';

@singleton()
export class ParentalPinStoreFacadeDepreciated {
  public get originalStore() {
    return container.resolve<ParentalPinStoreDepreciated>('parentalPinStoreDepreciated');
  }

  public get isParentalPinLocked() {
    return this.originalStore.isParentalPinLocked;
  }

  public get isAdultPinEntered() {
    return this.originalStore.isAdultPinEntered;
  }

  public get isParentalPinEntered() {
    return this.originalStore.isParentalPinEntered;
  }

  public get parentalPinTimeout() {
    return this.originalStore.parentalPinTimeout;
  }

  public get purchasePinEntered() {
    return this.originalStore.purchasePinEntered;
  }

  public get parentalPinEntered(): { [ParentalPinType.adult]: boolean; [ParentalPinType.parental]: boolean } {
    return this.originalStore.parentalPinEntered;
  }

  public get lastWatchedLinear() {
    return this.originalStore.lastWatchedLinear;
  }

  public get ageThreshold() {
    return this.originalStore.ageThreshold;
  }

  public get pinForPurchase() {
    return this.originalStore.pinForPurchase;
  }

  public get noThresholdAge() {
    return this.originalStore.noThresholdAge;
  }

  public init() {
    return this.originalStore.init();
  }

  public isBlockByParentRating<T>(
    asset: T & { id?: number; linear?: Linear; linearAssetId?: number; parentalRating: number },
  ) {
    return this.originalStore.isBlockByParentRating(asset);
  }

  public isBlockedByParentalRating(parentalRating: number) {
    return this.originalStore.isBlockedByParentalRating(parentalRating);
  }

  @action.bound
  public clearLastWatchedLinear() {
    return this.originalStore.clearLastWatchedLinear();
  }

  @action.bound
  public clearParentalPin(type: ParentalPinType) {
    return this.originalStore.clearParentalPin(type);
  }

  @action.bound
  async updateParentalPin(pin: string) {
    return this.originalStore.updateParentalPin(pin);
  }

  @action.bound
  async updatePurchasePin(pin: string) {
    return this.originalStore.updatePurchasePin(pin);
  }

  @action.bound
  async validateParentalPin(pin: string, type: ParentalPinType, channelId?: number) {
    return this.originalStore.validateParentalPin(pin, type, channelId);
  }

  @action.bound
  async validatePurchasePin(pin: string) {
    return this.originalStore.validatePurchasePin(pin);
  }

  @action.bound
  async checkMenuParentalPin(menuItem: KuxMenuItem) {
    return this.originalStore.checkMenuParentalPin(menuItem);
  }

  @action.bound
  async initModal() {
    return this.originalStore.initModal();
  }

  @action.bound
  /**
   * Destroy timer which is tracking lock time
   */
  public destroy() {
    return this.originalStore.destroy();
  }
}
